import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Bell, BellRing, ChevronLeft, ChevronRight, FileText, Film, Play } from "lucide-react";
import { toast } from "sonner";
import { api, DOMAINES, domaineLabel, errMsg, fmtDate, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import MediaUpload from "@/components/MediaUpload";
import { useAuth } from "@/context/AuthContext";

const MediaVignette = ({ media, className = "h-44" }) => {
  if (media.type?.startsWith("video/")) {
    return (
      <div className={`relative ${className} w-full bg-slate-900`}>
        <video src={mediaUrl(media.url)} className="h-full w-full object-cover opacity-90" muted playsInline preload="metadata" />
        <span className="absolute inset-0 grid place-items-center text-white/90">
          <Play size={26} />
        </span>
      </div>
    );
  }
  if (media.type === "application/pdf") {
    return (
      <div className={`${className} w-full bg-slate-100 grid place-items-center text-slate-500`}>
        <FileText size={26} />
      </div>
    );
  }
  return <img src={mediaUrl(media.url)} alt={media.nom || ""} className={`${className} w-full object-cover`} />;
};

const MediasRealisation = ({ medias, id }) => (
  <div className="grid grid-cols-3 gap-2 mt-3" data-testid={`realisation-medias-${id}`}>
    {medias.map((m) => (
      <a
        key={m.url}
        href={mediaUrl(m.url)}
        target="_blank"
        rel="noreferrer"
        data-testid={`realisation-media-${m.url.split("/").pop()}`}
        className="block rounded-lg overflow-hidden border border-slate-200 hover:border-[#005CFF] transition-colors"
      >
        <MediaVignette media={m} className="h-20" />
      </a>
    ))}
  </div>
);

const Carrousel = ({ items, onDevis }) => {
  const piste = useRef(null);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (items.length < 2) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % items.length), 5000);
    return () => clearInterval(timer);
  }, [items.length]);

  useEffect(() => {
    const enfant = piste.current?.children?.[index];
    if (enfant) piste.current.scrollTo({ left: enfant.offsetLeft - 8, behavior: "smooth" });
  }, [index]);

  if (!items.length) return null;

  return (
    <div className="space-y-3" data-testid="vitrine-carrousel">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="bk-label">Derniers ajouts</div>
          <p className="text-sm text-slate-600 mt-1">Nos chantiers les plus récents, en photos, PDF et vidéos.</p>
        </div>
        <div className="flex gap-1.5">
          <button
            data-testid="carrousel-prev"
            onClick={() => setIndex((i) => (i - 1 + items.length) % items.length)}
            className="h-9 w-9 grid place-items-center rounded-full border border-slate-200 text-slate-600 hover:border-slate-900 transition-colors"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            data-testid="carrousel-next"
            onClick={() => setIndex((i) => (i + 1) % items.length)}
            className="h-9 w-9 grid place-items-center rounded-full border border-slate-200 text-slate-600 hover:border-slate-900 transition-colors"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
      <div ref={piste} className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1 scrollbar-none">
        {items.map((r) => (
          <article
            key={r.id}
            data-testid={`carrousel-item-${r.id}`}
            className="snap-start shrink-0 w-[78%] sm:w-[46%] rounded-2xl overflow-hidden border border-slate-200 bg-white"
          >
            {r.image_url ? (
              <img src={r.image_url} alt={r.titre} className="h-40 w-full object-cover" />
            ) : r.medias?.length ? (
              <MediaVignette media={r.medias[0]} className="h-40" />
            ) : null}
            <div className="p-4">
              <div className="bk-label mb-1.5">
                {domaineLabel(r.domaine)} {r.ville ? `· ${r.ville}` : ""}
              </div>
              <h3 className="font-display text-base font-bold tracking-tight text-slate-900 truncate">{r.titre}</h3>
              <p className="text-xs text-slate-500 mt-1">{fmtDate(r.created_at)}</p>
              <Button data-testid={`carrousel-devis-${r.id}`} onClick={() => onDevis(r)} variant="outline" size="sm" className="mt-3 w-full rounded-xl">
                Demander un devis
              </Button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

const DevisDialog = ({ item, onClose }) => {
  const [message, setMessage] = useState("");
  const [medias, setMedias] = useState([]);
  const [busy, setBusy] = useState(false);

  const envoyer = async () => {
    setBusy(true);
    try {
      await api.post("/leads", {
        realisation_id: item?.id || null,
        message: message.trim() || `Devis similaire à : ${item?.titre || "—"}`,
        medias,
      });
      toast.success("Demande de devis transmise à BKPRO");
      onClose();
    } catch (e) {
      toast.error(errMsg(e, "Envoi impossible"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="devis-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Demander un devis</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Réalisation de référence : <strong>{item?.titre}</strong>. Ajoutez vos photos, plans PDF ou une courte vidéo, nous chiffrons plus vite.
          </p>
          <Textarea
            data-testid="devis-message-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            placeholder="Votre besoin, surface, délai souhaité…"
          />
          <MediaUpload medias={medias} onChange={setMedias} testid="devis-medias" label="Photos, PDF ou vidéo courte (6 max)" />
          <Button data-testid="devis-submit-btn" onClick={envoyer} disabled={busy} className="w-full h-12 rounded-xl bg-[#005CFF] hover:bg-[#0047CC]">
            {busy ? "Envoi…" : "Envoyer ma demande"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function Vitrine() {
  const { user, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [domaine, setDomaine] = useState(null);
  const [optin, setOptin] = useState(!!user?.optin);
  const [devisItem, setDevisItem] = useState(null);

  useEffect(() => {
    api.get("/realisations", { params: domaine ? { domaine } : {} }).then(({ data }) => setItems(data));
  }, [domaine]);

  const toggleOptin = async () => {
    const next = !optin;
    await api.post("/optin", { actif: next, canal: "email" });
    setOptin(next);
    refresh();
    toast.success(next ? "Vous serez informé des nouvelles réalisations" : "Notifications désactivées");
  };

  return (
    <div className="space-y-7" data-testid="vitrine-page">
      <div>
        <div className="bk-label mb-2">Nos dernières installations</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-900">Vitrine BKPRO</h1>
      </div>

      <button
        data-testid="optin-btn"
        onClick={toggleOptin}
        className={`w-full flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${optin ? "border-[#005CFF] bg-blue-50" : "border-slate-200 hover:border-slate-400"}`}
      >
        {optin ? <BellRing size={18} className="text-[#005CFF]" /> : <Bell size={18} className="text-slate-400" />}
        <span className="text-sm">
          <span className="block font-semibold text-slate-900">{optin ? "Notifications activées" : "Prévenez-moi des nouvelles réalisations"}</span>
          <span className="block text-xs text-slate-500">Email · désactivable à tout moment</span>
        </span>
      </button>

      <Carrousel items={items.slice(0, 8)} onDevis={setDevisItem} />

      <div className="flex gap-2 overflow-x-auto pb-1">
        <button
          data-testid="vitrine-filter-all"
          onClick={() => setDomaine(null)}
          className={`shrink-0 text-sm px-4 py-2 rounded-full border transition-colors ${!domaine ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
        >
          Tous
        </button>
        {DOMAINES.map((d) => (
          <button
            key={d.key}
            data-testid={`vitrine-filter-${d.key}`}
            onClick={() => setDomaine(d.key)}
            className={`shrink-0 text-sm px-4 py-2 rounded-full border transition-colors ${domaine === d.key ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
          >
            {d.label}
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        {items.map((r, i) => (
          <motion.article
            key={r.id}
            data-testid={`realisation-card-${r.id}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="rounded-2xl overflow-hidden border border-slate-200 bg-white flex flex-col"
          >
            {r.image_url && <img src={r.image_url} alt={r.titre} className="h-44 w-full object-cover" />}
            <div className="p-5 flex flex-col flex-1">
              <div className="bk-label mb-2">
                {domaineLabel(r.domaine)} {r.ville ? `· ${r.ville}` : ""}
              </div>
              <h2 className="font-display text-lg font-bold tracking-tight text-slate-900">{r.titre}</h2>
              <p className="text-sm text-slate-600 mt-2 flex-1">{r.description}</p>
              {r.video_url && (
                <a
                  href={mediaUrl(r.video_url)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`realisation-video-${r.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#005CFF] hover:underline mt-3"
                >
                  <Film size={13} /> Voir la vidéo du chantier
                </a>
              )}
              {r.medias?.length > 0 && <MediasRealisation medias={r.medias} id={r.id} />}
              <Button data-testid={`devis-btn-${r.id}`} onClick={() => setDevisItem(r)} variant="outline" className="mt-4 h-11 rounded-xl border-slate-300">
                <FileText size={15} className="mr-2" /> Demander un devis similaire
              </Button>
            </div>
          </motion.article>
        ))}
      </div>

      <DevisDialog item={devisItem} onClose={() => setDevisItem(null)} />
    </div>
  );
}
